import { useState, useRef, useEffect } from "react";
import "./PremiumMembership.scss";
import Logo from './logo.png';

// TODO: replace with your actual Google Form "formResponse" endpoint + entry IDs
const GOOGLE_FORM_ACTION_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSelOJg6ex6bLf8YeUbqdamsEn9hLuc9-b8FjcmH4Uq3z9mLCw/formResponse";

const GOOGLE_FORM_ENTRY_IDS = {
  fullName: "entry.497268553",
  email: "entry.1852330495",
  phone: "entry.212181651",
  linkedin: "entry.2018243479",
  designation: "entry.139784669",
  company: "entry.450965555",
  interests: "entry.1733354929",
};

const FEATURES = [
  { icon: "📚", label: "Exclusive Customer Success resources" },
  { icon: "📊", label: "Industry reports and research" },
  { icon: "🎓", label: "Premium webinars and expert sessions" },
  { icon: "🎙️", label: "Exclusive podcasts and member-only content" },
  { icon: "🌐", label: "Global CS networking" },
  { icon: "🧭", label: "Opportunities to become a Mentor" },
  { icon: "🎤", label: "Opportunities to become a Speaker" },
  { icon: "🏛️", label: "Become a Local Chapter Head" },
  { icon: "✍️", label: "Content collaboration opportunities" },
  { icon: "📻", label: "Podcast & Webinar guest opportunities" },
  { icon: "⚡", label: "Early access to community initiatives" },
  { icon: "🚀", label: "Career resources & growth" },
];

const INTEREST_OPTIONS = [
  "Mentorship",
  "Speaking opportunities",
  "Content collaboration",
  "Local chapter leadership",
  "Networking",
  "Career growth",
];

const INITIAL_FORM = {
  fullName: "",
  email: "",
  phone: "",
  linkedin: "",
  designation: "",
  company: "",
  interests: [],
};

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
}

function useRevealOnScroll() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
}

function Reveal({ as: Tag = "div", className = "", children, ...rest }) {
  const [ref, isVisible] = useRevealOnScroll();
  return (
    <Tag
      ref={ref}
      className={`reveal ${isVisible ? "reveal--visible" : ""} ${className}`.trim()}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export default function PremiumMembership() {
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isInterestOpen, setIsInterestOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsInterestOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const toggleInterest = (interest) => {
    setFormData((prev) => {
      const alreadySelected = prev.interests.includes(interest);
      return {
        ...prev,
        interests: alreadySelected
          ? prev.interests.filter((i) => i !== interest)
          : [...prev.interests, interest],
      };
    });
  };

  const isFormValid =
    formData.fullName.trim() &&
    formData.email.trim() &&
    formData.phone.trim() &&
    formData.linkedin.trim() &&
    formData.designation.trim() &&
    formData.company.trim() &&
    formData.interests.length > 0;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isFormValid || status === "submitting") return;

    setStatus("submitting");

    try {
      const body = new URLSearchParams();
      body.append(GOOGLE_FORM_ENTRY_IDS.fullName, formData.fullName);
      body.append(GOOGLE_FORM_ENTRY_IDS.email, formData.email);
      body.append(GOOGLE_FORM_ENTRY_IDS.phone, formData.phone);
      body.append(GOOGLE_FORM_ENTRY_IDS.linkedin, formData.linkedin);
      body.append(GOOGLE_FORM_ENTRY_IDS.designation, formData.designation);
      body.append(GOOGLE_FORM_ENTRY_IDS.company, formData.company);
      formData.interests.forEach((interest) => {
        body.append(GOOGLE_FORM_ENTRY_IDS.interests, interest);
      });

      console.log("formData", formData);

      for (const [key, value] of body.entries()) {
        console.log(key, value);
      }

      await fetch(GOOGLE_FORM_ACTION_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      setStatus("success");
      setFormData(INITIAL_FORM);
    } catch (err) {
      console.error("Membership form submission failed:", err);
      setStatus("error");
    }
  };

  const scrollToForm = (event) => {
    event.preventDefault();
    document
      .getElementById("membership-form")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const interestButtonLabel = formData.interests.length
    ? formData.interests.join(", ")
    : "Select one or more…";

  return (
    <main className="premium-page">
      <header className="premium-header">
        <div className="premium-header__inner">
          <a href="#top" className="premium-header__logo">
            <img
              src={Logo}
              alt="Consortia — Connecting Minds Behind Customer Success"
              draggable="false"
            />
          </a>

          {!isMobile && (
            <nav className="premium-header__nav">
              <a href="#about">About</a>
              <a href="#early-access">Access</a>
              <a href="mailto:hello@consortia.com">Contact</a>
            </nav>
          )}

          <a
            href="#membership-form"
            className="btn-primary premium-header__cta"
            onClick={scrollToForm}
          >
            Request Access
          </a>
        </div>
      </header>

      <section className="premium-hero">
        <div className="premium-hero__glow" aria-hidden="true" />
        <Reveal className="premium-hero__content">
          <div className="pill">Premium Membership</div>
          <h1 className="premium-hero__title">
            Be Among the First{" "}
            <span className="text-gradient">Premium Members</span>
          </h1>
          <p className="premium-hero__subtitle">
            Unlock exclusive opportunities to learn, connect, contribute, and
            grow with a global Customer Success community.
          </p>
          <div className="premium-hero__actions">
            <button
              type="button"
              className="btn-primary premium-hero__cta"
              onClick={scrollToForm}
            >
              Become a Premium Member
            </button>
            <div className="premium-hero__price">
              <span>Membership</span>
              <span className="text-gradient premium-hero__price-value">
                $10
              </span>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="premium-features">
        <div className="premium-features__inner">
          <Reveal className="premium-features__heading">
            <h2>
              What&apos;s inside <span className="text-gradient">Premium</span>
            </h2>
            <p>Everything you need to grow, connect, and lead in Customer Success.</p>
          </Reveal>

          <div className="premium-features__grid">
            {FEATURES.map((feature) => (
              <Reveal key={feature.label} className="feature-card">
                <div className="feature-card__icon">
                  <span>{feature.icon}</span>
                </div>
                <p>{feature.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="membership-form" className="premium-form-section">
        <div className="premium-form-section__inner">
          <Reveal className="premium-form-section__heading">
            <h2>
              Apply for <span className="text-gradient">Premium Membership</span>
            </h2>
            <p>Tell us about yourself. We&apos;ll review your application and complete your membership.</p>
          </Reveal>

          <Reveal as="form" className="membership-form" onSubmit={handleSubmit}>
            <div className="form-field">
              <label htmlFor="fullName">Full Name</label>
              <input
                id="fullName"
                type="text"
                required
                maxLength={255}
                placeholder="Your full name"
                value={formData.fullName}
                onChange={handleChange("fullName")}
              />
            </div>

            <div className="form-field">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                required
                maxLength={255}
                placeholder="you@company.com"
                value={formData.email}
                onChange={handleChange("email")}
              />
            </div>

            <div className="form-field">
              <label htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                type="tel"
                required
                maxLength={255}
                placeholder="+1 555 000 0000"
                value={formData.phone}
                onChange={handleChange("phone")}
              />
            </div>

            <div className="form-field">
              <label htmlFor="linkedin">LinkedIn Profile URL</label>
              <input
                id="linkedin"
                type="url"
                required
                maxLength={255}
                placeholder="linkedin.com/in/…"
                value={formData.linkedin}
                onChange={handleChange("linkedin")}
              />
            </div>

            <div className="form-field">
              <label htmlFor="designation">Designation</label>
              <input
                id="designation"
                type="text"
                required
                maxLength={255}
                placeholder="VP Customer Success"
                value={formData.designation}
                onChange={handleChange("designation")}
              />
            </div>

            <div className="form-field">
              <label htmlFor="company">Company Name</label>
              <input
                id="company"
                type="text"
                required
                maxLength={255}
                placeholder="Where you work"
                value={formData.company}
                onChange={handleChange("company")}
              />
            </div>

            <div className="form-field" ref={dropdownRef}>
              <label>Your Interest Areas</label>
              <div className="interest-dropdown">
                <button
                  type="button"
                  className="interest-dropdown__trigger"
                  onClick={() => setIsInterestOpen((open) => !open)}
                  aria-expanded={isInterestOpen}
                >
                  <span className={formData.interests.length ? "" : "placeholder"}>
                    {interestButtonLabel}
                  </span>
                  <span aria-hidden="true" className="chevron">▾</span>
                </button>

                {/* {isInterestOpen && (
                  <ul className="interest-dropdown__menu">
                    {INTEREST_OPTIONS.map((interest) => (
                      <li key={interest}>
                        <label>
                          <input
                            type="checkbox"
                            checked={formData.interests.includes(interest)}
                            onChange={() => toggleInterest(interest)}
                          />
                          {interest}
                        </label>
                      </li>
                    ))}
                  </ul>
                )} */}

                {isInterestOpen && (
                  <ul className="interest-dropdown__menu">
                    {INTEREST_OPTIONS.map((interest) => {
                      const isSelected = formData.interests.includes(interest);

                      return (
                        <li
                          key={interest}
                          onClick={() => toggleInterest(interest)}
                          className={isSelected ? "selected" : ""}
                        >
                          <span className="interest-text">{interest}</span>

                          <span className={`interest-check ${isSelected ? "active" : ""}`}>
                            {isSelected && "✓"}
                          </span>

                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            hidden
                          />
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={!isFormValid || status === "submitting"}
              className="btn-primary membership-form__submit"
            >
              {status === "submitting" ? "Submitting…" : "Continue"}
            </button>

            {!formData.interests.length && (
              <p className="form-hint">Please select at least one interest area.</p>
            )}
            {status === "success" && (
              <p className="form-hint form-hint--success">
                Thanks! Your application has been received.
              </p>
            )}
            {status === "error" && (
              <p className="form-hint form-hint--error">
                Something went wrong. Please try again.
              </p>
            )}

            <p className="membership-form__back">
              <a href="/">← Back to home</a>
            </p>
          </Reveal>
        </div>
      </section>

      <footer className="premium-footer">
        <div className="premium-footer__inner">
          <img
            src="/assets/consortia-logo.png"
            alt="Consortia — Connecting Minds Behind Customer Success"
            draggable="false"
          />
          <nav>
            <a href="https://www.linkedin.com" target="_blank" rel="noreferrer">LinkedIn</a>
            <a href="https://www.instagram.com" target="_blank" rel="noreferrer">Instagram</a>
            <a href="mailto:hello@consortia.com">Email</a>
          </nav>
          <p>© {new Date().getFullYear()} Consortia</p>
        </div>
      </footer>
    </main>
  );
}
